import { TIME_OPTIONS_15 } from '@/routes/Agency/CreateOrderV2/Steps/ScheduleStep/timePicker';
import type {
  OpsManager,
  OpsManagerSummary,
  OpsSchedule,
} from '@/services/opsRota/types';

// Times and form values for the ops rota (GRAVY-10878). graphy takes and
// returns "HH:MM" (24 h, ops zone).

const pad = (n: number) => String(n).padStart(2, '0');

/** Quarter-hour options: value "HH:MM", label "6:00 AM". */
export const TIME_OPTIONS = TIME_OPTIONS_15.map((label, i) => ({
  value: `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`,
  label,
}));

/** "08:00" → "8:00 AM", "17:30" → "5:30 PM", "00:15" → "12:15 AM". */
export const clockLabel = (clock: string): string => {
  const [h, m] = clock.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return clock;
  return `${h % 12 || 12}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
};

/** "8am", "8:30pm": the compact form the preview card uses (UI spec §6). */
export const compactClock = (clock: string): string => {
  const [h, m] = clock.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return clock;
  return `${h % 12 || 12}${m === 0 ? '' : `:${pad(m)}`}${h < 12 ? 'am' : 'pm'}`;
};

// ---- the add/edit form -------------------------------------------------

/**
 * The picker's list (UI spec §9.2): the same 96 quarter hours, starting at
 * 6:00 AM and wrapping past midnight to 5:45 AM. Start and End share it; End
 * is not filtered by Start.
 */
export const PICKER_OPTIONS = [
  ...TIME_OPTIONS.slice(24),
  ...TIME_OPTIONS.slice(0, 24),
];

/** Display-only placeholders (UI spec §9.1); never values. */
export const START_PLACEHOLDER = '06:00';
export const END_PLACEHOLDER = '15:00';

/**
 * The list row for a clock. An off-grid time from graphy ("09:07") falls
 * back to the quarter hour before it ("09:00"). -1 for '' or garbage.
 */
export const pickerIndex = (clock: string): number => {
  const [h, m] = clock.split(':').map(Number);
  if (clock === '' || Number.isNaN(h) || Number.isNaN(m)) return -1;
  const snapped = `${pad(h)}:${pad(Math.floor(m / 15) * 15)}`;
  return PICKER_OPTIONS.findIndex((o) => o.value === snapped);
};

/** Row height of a picker item (UI spec §9.2). */
export const PICKER_ROW_PX = 36;

/**
 * Where the list opens: the current value (or the placeholder when unset)
 * as the second visible row. Start empty → 0, End empty → 1260, 8:00 AM → 252.
 */
export const pickerScrollTop = (value: string, placeholder: string): number =>
  Math.max(
    0,
    pickerIndex(value || placeholder) * PICKER_ROW_PX - PICKER_ROW_PX,
  );

export type ScheduleFormValues = {
  /** "HH:MM", or '' while unset. */
  startTime: string;
  endTime: string;
  /** Applied managers, in chip order. */
  managerIds: number[];
};

export const EMPTY_FORM: ScheduleFormValues = {
  startTime: '',
  endTime: '',
  managerIds: [],
};

export const fromSchedule = (s: OpsSchedule): ScheduleFormValues => ({
  startTime: s.start_time,
  endTime: s.end_time,
  managerIds: s.managers.map((m) => m.id),
});

export const toTimes = (v: ScheduleFormValues) => ({
  start_time: v.startTime,
  end_time: v.endTime,
  manager_ids: v.managerIds,
});

/**
 * Both times, different from each other, and at least one manager. An
 * end before the start is valid: the block runs into the next day (BR-01).
 */
export const canSave = (v: ScheduleFormValues): boolean =>
  v.startTime !== '' &&
  v.endTime !== '' &&
  v.startTime !== v.endTime &&
  v.managerIds.length > 0;

/** "HH:MM" strings compare correctly as text. */
export const crossesMidnight = (v: ScheduleFormValues): boolean =>
  v.startTime !== '' && v.endTime !== '' && v.endTime < v.startTime;

const sameIds = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

export const isDirty = (
  v: ScheduleFormValues,
  initial: ScheduleFormValues,
): boolean =>
  v.startTime !== initial.startTime ||
  v.endTime !== initial.endTime ||
  !sameIds(v.managerIds, initial.managerIds);

/**
 * BR-06: not on the active roster or, before the roster loads, flagged
 * inactive on the block. Gates the save and flags the chip.
 */
export const isInactiveManager = (
  id: number,
  roster: OpsManager[] | undefined,
  assigned: OpsManagerSummary[] = [],
): boolean =>
  roster
    ? !roster.some((m) => m.id === id)
    : assigned.find((m) => m.id === id)?.active === false;
